import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/components/public';
import { ChevronDown, ChevronUp, ArrowRight, HelpCircle, Search } from 'lucide-react';

import { t } from '@/i18n';
const FAQ_CATEGORIES = [
  {
    name: t("Getting Started"),
    faqs: [
      {
        q: t("What is a preliminary notice?"),
        a: t("A preliminary notice (also called a pre-lien notice, notice to owner, or preliminary 20-day notice) is a document that notifies property owners and other parties that you are working on their project and have lien rights. It's required in many states to preserve your right to file a mechanic's lien if you don't get paid.")
      },
      {
        q: t("Do I need to send a preliminary notice?"),
        a: t("In most states, yes. Preliminary notices are required to protect your lien rights, especially if you're a subcontractor or supplier without a direct contract with the property owner. Even in states where it's not required, sending a notice is a best practice that often improves payment speed.")
      },
      {
        q: t("How do I get started?"),
        a: t("Simply create a free account, submit your project details, and we'll handle the rest — we research the property owner, general contractor and every other recipient for you.")
      },
      {
        q: t("How does pricing work?"),
        a: t("Pricing is simple: $35–$45 per notice plus certified mail postage. No subscription, no setup fee, no volume commitment — you only pay when you send a notice.")
      },
    ]
  },
  {
    name: t("Pricing & Payment"),
    faqs: [
      {
        q: t("How does your pricing work?"),
        a: 'Our pricing is simple: Document Fee + (Postage Rate × Number of Recipients) = Total. For example, a preliminary notice ($45) sent to 2 recipients via certified mail ($10.44 each) would cost $65.88. At 25 or more notices a month the document fee drops to $35.'
      },
      {
        q: t("Are there any monthly fees or subscriptions?"),
        a: t("No! We believe in transparent, pay-as-you-go pricing. There are no monthly fees, no annual subscriptions, and no setup costs. You only pay for the notices you actually send.")
      },
      {
        q: t("What payment methods do you accept?"),
        a: t("We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure Stripe payment processing. Payment is collected after you approve the notice for sending.")
      },
      {
        q: t("Is research included?"),
        a: t("Yes. Every preliminary notice ($45, or $35 at 25+ a month) includes research – we find the property owner, general contractor, and other required recipients.")
      },
    ]
  },
  {
    name: t("Notice Process"),
    faqs: [
      {
        q: t("How long does it take to send a notice?"),
        a: t("Most notices are processed within 24-48 business hours. Full-service orders that require extensive research may take slightly longer. You'll receive email updates at each step of the process.")
      },
      {
        q: t("How is my notice delivered?"),
        a: t("Notices are sent via USPS certified mail with electronic return receipt. This provides proof of mailing and delivery, which is essential for legal compliance. You can track delivery status in your dashboard.")
      },
      {
        q: t("What information do I need to provide?"),
        a: t("At minimum, you'll need: your company information, the project/job site address, the type of work you're performing, and the contract amount.")
      },
      {
        q: t("Can I review my notice before it's sent?"),
        a: t("Absolutely! You'll receive a preview of your notice and must approve it before we send. This ensures accuracy and gives you peace of mind.")
      },
    ]
  },
  {
    name: t("State Requirements"),
    faqs: [
      {
        q: t("Do you cover all 50 states?"),
        a: t("Yes! We generate state-compliant preliminary notices, mechanic's liens, and bond claims for all 50 states. Each document is tailored to meet your state's specific legal requirements.")
      },
      {
        q: t("How do I know my state's deadline?"),
        a: t("Each state has different deadline requirements. Our platform automatically tracks deadlines based on your project details. You'll receive reminders before critical deadlines to ensure you don't miss any important dates.")
      },
      {
        q: t("What if my state doesn't require a preliminary notice?"),
        a: t("Even if your state doesn't legally require a preliminary notice, sending one is still a best practice. It notifies all parties of your involvement and often speeds up payment by establishing clear communication from the start.")
      },
    ]
  },
  {
    name: t("Account & Support"),
    faqs: [
      {
        q: t("Can I add team members to my account?"),
        a: t("Yes! Master accounts can add sub-users with customizable permissions. This is perfect for larger companies that need multiple people to submit and track orders.")
      },
      {
        q: t("How do I access my past notices?"),
        a: t("All your notices, documents, and proof of service records are stored securely in your dashboard. You can access, download, and print them anytime.")
      },
      {
        q: t("What if I have questions or need help?"),
        a: t("Our dedicated support team is here to help! Contact us via phone, email, or through your dashboard. We pride ourselves on responsive, knowledgeable customer service.")
      },
    ]
  },
];

const FAQPage = () => {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const categoryRefs = useRef({});
  const contentRef = useRef(null);

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_CATEGORIES.flatMap(category => 
      category.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  // Organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "All American Notice",
    "url": "https://allamericannotice.com",
    "logo": "https://allamericannotice.com/og-image.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-561-680-2857",
      "contactType": "customer service",
      "areaServed": "US",
      "availableLanguage": "English"
    },
    "sameAs": []
  };

  const toggleItem = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle category selection with scroll
  const handleCategoryClick = (categoryName) => {
    setActiveCategory(categoryName);
    
    // If selecting a specific category, scroll to it after a brief delay
    // (to allow the filter to apply first)
    if (categoryName !== 'all') {
      setTimeout(() => {
        const categoryIndex = FAQ_CATEGORIES.findIndex(c => c.name === categoryName);
        const element = categoryRefs.current[categoryIndex];
        if (element) {
          const headerOffset = 160; // Account for sticky header + tabs
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // If "All" is selected, scroll to top of content
      if (contentRef.current) {
        const headerOffset = 160;
        const elementPosition = contentRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const filteredCategories = FAQ_CATEGORIES.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => 
    activeCategory === 'all' || category.name === activeCategory
  ).filter(category => category.faqs.length > 0);

  return (
    <PublicLayout>
      <Helmet>
        <title>FAQ - Preliminary Notice & Mechanics Lien Questions | All American Notice</title>
        <meta name="description" content="Find answers to common questions about preliminary notices, mechanics liens, bond claims, and construction payment rights. Learn deadlines, requirements, and how to protect your payments." />
        <meta name="keywords" content="preliminary notice FAQ, mechanics lien questions, construction lien help, notice to owner FAQ, payment protection, lien rights, construction law FAQ" />
        <link rel="canonical" href="https://allamericannotice.com/faq" />
        <meta property="og:title" content="FAQ - Preliminary Notice & Mechanics Lien Questions | All American Notice" />
        <meta property="og:description" content="Find answers to common questions about preliminary notices, mechanics liens, and construction payment rights." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://allamericannotice.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="faq-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#F97316] text-white mb-4">{t("Help Center")}</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {t("Frequently Asked Questions")}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            {t("Find answers to common questions about preliminary notices, lien rights, and our services.")}
          </p>
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder={t("Search for answers...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316]"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-4 bg-slate-50 border-b border-slate-200 sticky top-16 lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={activeCategory === 'all' ? 'default' : 'outline'}
              onClick={() => handleCategoryClick('all')}
              className={activeCategory === 'all' ? 'bg-[#1e3a5f]' : ''}
              size="sm"
              data-testid="faq-filter-all"
            >
              All
            </Button>
            {FAQ_CATEGORIES.map((category) => (
              <Button
                key={category.name}
                variant={activeCategory === category.name ? 'default' : 'outline'}
                onClick={() => handleCategoryClick(category.name)}
                className={activeCategory === category.name ? 'bg-[#1e3a5f]' : ''}
                size="sm"
                data-testid={`faq-filter-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white" data-testid="faq-content" ref={contentRef}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">{t("No results found")}</h3>
              <p className="text-slate-500">{t("Try adjusting your search or browse all categories.")}</p>
              <Button 
                variant="outline" 
                onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                className="mt-4"
              >
                {t("Clear filters")}
              </Button>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredCategories.map((category, categoryIndex) => {
                // Find the original index for the ref
                const originalIndex = FAQ_CATEGORIES.findIndex(c => c.name === category.name);
                return (
                  <div 
                    key={categoryIndex} 
                    ref={el => categoryRefs.current[originalIndex] = el}
                    id={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-3">
                      <span className="w-8 h-8 rounded-lg bg-[#F97316] text-white flex items-center justify-center text-sm font-bold">
                        {category.faqs.length}
                      </span>
                      {category.name}
                    </h2>
                    <div className="space-y-4">
                      {category.faqs.map((faq, faqIndex) => {
                        const key = `${originalIndex}-${faqIndex}`;
                        const isOpen = openItems[key];
                        return (
                          <div 
                            key={faqIndex}
                            className={`border-2 rounded-xl overflow-hidden transition-all ${isOpen ? 'border-[#1e3a5f] shadow-md' : 'border-slate-200'}`}
                          >
                            <button
                              onClick={() => toggleItem(originalIndex, faqIndex)}
                              className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
                              data-testid={`faq-item-${originalIndex}-${faqIndex}`}
                            >
                              <span className="font-semibold text-[#1e3a5f] pr-4">{faq.q}</span>
                              {isOpen ? (
                                <ChevronUp className="h-5 w-5 text-[#F97316] flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                              )}
                            </button>
                            {isOpen && (
                              <div className="px-5 pb-5 bg-slate-50 border-t border-slate-100">
                                <p className="text-slate-600 leading-relaxed pt-4">{faq.a}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-4">{t("Still Have Questions?")}</h2>
          <p className="text-slate-600 mb-8">
            {t("Our team is here to help. Contact us and we'll get back to you as soon as possible.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-[#F97316] hover:bg-[#ea6a10]"
            >
              {t("Contact Us")}
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/register')}
              className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
            >
              {t("Get Started")}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Ready to Protect Your Lien Rights?")}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t("Send your first notice with confidence — no subscription, no setup fee.")}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')} 
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-lg px-10 h-14 font-semibold"
          >
            {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FAQPage;
